/**
 * SmartIntakeWizard - Specialized Buyer Agent Interface
 * Provides FOUND/INFERRED/MISSING gap analysis with user confirmation
 */

import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle, CheckCircle2, AlertCircle, HelpCircle, FileText, Upload, Brain, ArrowRight, Edit2, Check, X } from 'lucide-react';
import { toastSuccess, toastError, toastInfo } from '@/lib/toast';
import api from '@/lib/api';
import { procurementTypeLabel } from '@/lib/procurementTypeLabel';
import { AiProcessingNotice } from '@/lib/aiProcessingNotices';

// Status badge colors
const STATUS_COLORS = {
  FOUND: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  INFERRED: 'bg-amber-100 text-amber-800 border-amber-200',
  MISSING: 'bg-red-100 text-red-800 border-red-200'
};

const STATUS_ICONS = {
  FOUND: CheckCircle,
  INFERRED: HelpCircle,
  MISSING: AlertCircle
};

// Field display names
const FIELD_LABELS = {
  item_description: 'Item / Service Description',
  quantity: 'Quantity',
  uom: 'Unit of Measure',
  technical_specifications: 'Technical Specifications',
  delivery_location: 'Delivery Location',
  need_by_date: 'Need By Date',
  procurement_type: 'Procurement Type',
  warranty_requirement: 'Warranty Requirement',
  budget: 'Budget',
  currency: 'Currency',
  licensing_model: 'Licensing Model',
  end_user_profile: 'End User Profile',
  activation_date: 'Activation / Go-live Date',
  security_compliance: 'Security & Compliance',
  integration_requirements: 'Integration Requirements',
  deployment_scope: 'Deployment Scope',
  amc_support_tier: 'AMC / Support Tier',
  installation_requirement: 'Installation Requirement',
  amc_duration: 'AMC Duration',
  service_start_date: 'Service Start Date',
  service_end_date: 'Service End Date',
  site_constraints: 'Site Constraints',
  compliance_safety: 'Compliance / Safety'
};

// Fields that should use master data dropdowns
const DROPDOWN_FIELDS = {
  currency: 'currencies',
  delivery_location: 'locations',
  uom: 'uom'
};

// Static options for fields without master data
const STATIC_OPTIONS = {
  procurement_type: ['goods', 'services', 'mixed'],
  licensing_model: ['Per user', 'Per device', 'Consumption-based', 'Enterprise agreement', 'Perpetual'],
  end_user_profile: ['Office productivity', 'Developer/Engineering', 'Field/Remote workers', 'Executive', 'Shared pool'],
  deployment_scope: ['Bare hardware supply only', 'Supply + imaging', 'Supply + imaging + MDM enrolment'],
  amc_support_tier: ['Standard 1 year', 'Extended 2-3 years', 'On-site next-business-day support'],
  installation_requirement: ['Supply only', 'Supply + Installation', 'Supply + Installation + Commissioning'],
  amc_duration: ['1 year', '2 years', '3 years', 'As per warranty'],
  uom: ['Each', 'Unit', 'Piece', 'Pack', 'Box', 'Set', 'Lot', 'Kg', 'Litre', 'Metre', 'Sqft', 'Hours', 'Days', 'Weeks', 'Months', 'Years', 'License', 'User', 'Visit', 'Job']
};

export const SmartIntakeWizard = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1); // 1: Input, 2: Review, 3: Questions, 4: Confirm
  const [loading, setLoading] = useState(false);
  const [userRequirement, setUserRequirement] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Master data for dropdowns
  const [masterData, setMasterData] = useState({
    currencies: [],
    locations: {},
    categories: {}
  });

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toastError('File size must be less than 10MB');
      return;
    }

    // Add file to list (actual upload happens during analysis)
    setUploadedFiles(prev => [...prev, file]);
    toastSuccess(`${file.name} attached`);
    
    // Reset input
    e.target.value = '';
  };

  // Remove uploaded file
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Fetch master data on mount
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const response = await api.getLookups();
        if (response.data) {
          setMasterData({
            currencies: response.data.currencies || [],
            locations: response.data.locations || {},
            categories: response.data.categories || {}
          });
        }
      } catch (error) {
        console.error('Failed to fetch master data:', error);
      }
    };
    fetchMasterData();
  }, []);

  // Helper to check if a field should use dropdown
  const getFieldOptions = (fieldName) => {
    // Check if it's a master data field
    if (fieldName === 'currency') {
      // Currency is a static list since API doesn't return it
      return [
        { value: 'INR', label: 'INR - Indian Rupee' },
        { value: 'USD', label: 'USD - US Dollar' },
        { value: 'EUR', label: 'EUR - Euro' },
        { value: 'GBP', label: 'GBP - British Pound' },
        { value: 'AED', label: 'AED - UAE Dirham' },
        { value: 'SGD', label: 'SGD - Singapore Dollar' },
        { value: 'AUD', label: 'AUD - Australian Dollar' }
      ];
    }
    if (fieldName === 'delivery_location') {
      // Convert location lookup object to array of options
      const locations = masterData.locations || {};
      if (typeof locations === 'object' && Object.keys(locations).length > 0) {
        return Object.entries(locations).map(([code, label]) => ({
          value: code,
          label: label
        }));
      }
      return null;
    }
    // Check static options
    if (STATIC_OPTIONS[fieldName]) {
      return STATIC_OPTIONS[fieldName].map(opt => ({
        value: opt,
        // TYPE LABEL PARITY PATCH (2026-07-28 ruling): procurement_type's
        // options must show the friendly label, not the raw enum - the
        // stored `value` (used on save) stays the raw enum either way.
        label: fieldName === 'procurement_type' ? procurementTypeLabel(opt) : opt
      }));
    }
    return null;
  };

  // Step 1: Analyze requirement
  const handleAnalyze = useCallback(async () => {
    if (!userRequirement.trim()) {
      toastError('Please enter your procurement requirement');
      return;
    }

    setLoading(true);
    try {
      // Process uploaded files - extract text content for analysis
      let attachedDocuments = null;
      if (uploadedFiles.length > 0) {
        attachedDocuments = [];
        for (const file of uploadedFiles) {
          // For now, include file metadata. In production, these would be uploaded first.
          attachedDocuments.push({
            filename: file.name,
            file_type: file.type || 'application/octet-stream',
            size: file.size
          });
        }
        toastInfo(`Analyzing requirement with ${uploadedFiles.length} attached document(s)...`);
      }
      
      const response = await api.smartIntakeAnalyze(userRequirement, attachedDocuments);
      setSessionId(response.data.session_id);
      setAnalysisResult(response.data);
      setStep(2);
      toastSuccess(`Routed to ${response.data.agent_type} Buyer Agent`);
    } catch (error) {
      console.error('Smart intake analysis failed:', error);
      toastError('Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userRequirement, uploadedFiles]);

  // Handle field edit
  const handleStartEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const handleSaveEdit = (field) => {
    if (editValue.trim()) {
      setUserAnswers(prev => ({
        ...prev,
        [field]: editValue.trim()
      }));
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  // Handle gap question answer
  const handleAnswerChange = (field, value) => {
    setUserAnswers(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Step 3: Confirm and create procurement
  const handleConfirmAndCreate = useCallback(async () => {
    setLoading(true);
    try {
      // First confirm the intake
      await api.smartIntakeConfirm(sessionId, userAnswers);
      
      // Then create the procurement
      const response = await api.smartIntakeCreateProcurement(sessionId);
      
      toastSuccess(`Procurement ${response.data.serial_id} created!`);
      
      if (onComplete) {
        onComplete(response.data);
      }
    } catch (error) {
      console.error('Failed to create procurement:', error);
      toastError('Failed to create procurement. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, userAnswers, onComplete]);

  // Render Step 1: Input
  const renderInputStep = () => (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#0055FF]/10 rounded-lg">
            <Brain className="w-6 h-6 text-[#0055FF]" />
          </div>
          <div>
            <CardTitle>Smart Intake</CardTitle>
            <CardDescription>
              Our AI will analyze your requirement and extract key information
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-zinc-700 mb-2">
            Describe your procurement requirement
          </label>
          <Textarea
            value={userRequirement}
            onChange={(e) => setUserRequirement(e.target.value)}
            placeholder="Example: We need 100 laptops with Intel i7 processor, 16GB RAM, 512GB SSD for our new office in Delhi. Delivery required by end of August. Please include 3-year warranty and on-site support."
            rows={6}
            className="resize-none"
            data-testid="smart-intake-requirement-input"
          />
          <div className="mt-2">
            <AiProcessingNotice />
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="border border-dashed border-zinc-300 rounded-lg p-4 bg-zinc-50">
          <div className="flex items-center gap-3 mb-3">
            <Upload className="w-5 h-5 text-zinc-500" />
            <div>
              <p className="text-sm font-medium text-zinc-700">Attach Supporting Documents (Optional)</p>
              <p className="text-xs text-zinc-500">Upload BOQ, specifications, or requirement documents for better analysis</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.doc,.docx,.txt"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="smart-intake-file-upload"
              />
              <span className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors">
                <FileText className="w-4 h-4 text-zinc-500" />
                Choose File
              </span>
            </label>
            
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map((file, idx) => (
                  <div 
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-50 border border-green-200 rounded-md"
                  >
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 truncate max-w-[150px]">{file.name}</span>
                    <button
                      onClick={() => removeFile(idx)}
                      className="text-green-600 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <p className="text-xs text-zinc-400 mt-2">
            Supported: CSV, Excel, PDF, Word, Text (Max 10MB)
          </p>
          <div className="mt-2">
            <AiProcessingNotice />
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={handleAnalyze} 
          disabled={loading || !userRequirement.trim()}
          data-testid="smart-intake-analyze-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              Analyze Requirement
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );

  // Render Step 2: Review extracted fields
  const renderReviewStep = () => {
    if (!analysisResult) return null;

    const { extraction, gap_analysis, routing, agent_type } = analysisResult;
    const extractedFields = extraction?.extracted_fields || [];
    const conflicts = extraction?.conflicts || [];
    const questions = gap_analysis?.questions || [];

    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Routing Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${agent_type === 'IT' ? 'bg-blue-100' : agent_type === 'FACILITIES' ? 'bg-orange-100' : 'bg-zinc-100'}`}>
                  <Brain className={`w-5 h-5 ${agent_type === 'IT' ? 'text-blue-600' : agent_type === 'FACILITIES' ? 'text-orange-600' : 'text-zinc-600'}`} />
                </div>
                <div>
                  <p className="font-medium">
                    {agent_type === 'IT' ? 'IT & Technology' : agent_type === 'FACILITIES' ? 'Facilities & Infrastructure' : 'General'} Buyer Agent
                  </p>
                  <p className="text-sm text-zinc-500">
                    {routing?.reasoning}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className={routing?.confidence === 'high' ? 'border-emerald-500 text-emerald-700' : 'border-amber-500 text-amber-700'}>
                {routing?.confidence} confidence
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Understanding Summary</CardTitle>
            <CardDescription>{extraction?.summary}</CardDescription>
          </CardHeader>
        </Card>

        {/* Conflicts (if any) */}
        {conflicts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-lg text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Conflicts Detected
              </CardTitle>
              <CardDescription className="text-amber-700">
                Please resolve these conflicts before proceeding
              </CardDescription>
            </CardHeader>
            <CardContent>
              {conflicts.map((conflict, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg border border-amber-200 mb-2">
                  <p className="font-medium">{FIELD_LABELS[conflict.field] || conflict.field}</p>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-xs text-zinc-500">From your text:</p>
                      <p className="text-sm">
                        {conflict.field === 'procurement_type' ? procurementTypeLabel(conflict.user_text_value) : conflict.user_text_value}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">From document:</p>
                      <p className="text-sm">
                        {conflict.field === 'procurement_type' ? procurementTypeLabel(conflict.document_value) : conflict.document_value}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Extracted Fields Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Extracted Information</CardTitle>
            <CardDescription>
              Review and correct if needed. Click the edit icon to modify any value.
            </CardDescription>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> The more specific your requirement, the better the RFP. Consider adding details like exact specifications, quantities, delivery timelines, and location.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-700">Field</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-700">Value</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-zinc-700">Source</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-zinc-700">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-zinc-700">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {extractedFields.map((field, idx) => {
                    const StatusIcon = STATUS_ICONS[field.status] || HelpCircle;
                    const isEditing = editingField === field.field;
                    const hasUserAnswer = userAnswers[field.field];
                    const rawDisplayValue = hasUserAnswer || field.value;
                    // TYPE LABEL PARITY PATCH (2026-07-28 ruling): the raw
                    // enum ("mixed") must never render here - internal
                    // value (userAnswers/field.value) is untouched.
                    const displayValue = field.field === 'procurement_type'
                      ? procurementTypeLabel(rawDisplayValue)
                      : rawDisplayValue;

                    return (
                      <tr key={idx} className="hover:bg-zinc-50">
                        <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                          {FIELD_LABELS[field.field] || field.field}
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-700">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              {(() => {
                                const options = getFieldOptions(field.field);
                                if (options && options.length > 0) {
                                  // Render dropdown for fields with options
                                  return (
                                    <Select
                                      value={editValue}
                                      onValueChange={(val) => setEditValue(val)}
                                    >
                                      <SelectTrigger className="h-8 w-full min-w-[200px]">
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {options.map((opt, i) => (
                                          <SelectItem key={i} value={opt.value}>
                                            {opt.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                } else if (field.field === 'need_by_date' || field.field === 'activation_date' || field.field === 'service_start_date' || field.field === 'service_end_date') {
                                  // Date input
                                  return (
                                    <Input
                                      type="date"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="h-8"
                                      autoFocus
                                    />
                                  );
                                } else {
                                  // Default text input
                                  return (
                                    <Input
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="h-8"
                                      autoFocus
                                    />
                                  );
                                }
                              })()}
                              <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(field.field)}>
                                <Check className="w-4 h-4 text-emerald-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <span className={!displayValue ? 'text-zinc-400 italic' : ''}>
                              {displayValue || 'Not found'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-500">
                          {hasUserAnswer ? 'User corrected' : field.source}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={`${STATUS_COLORS[hasUserAnswer ? 'FOUND' : field.status]} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {hasUserAnswer ? 'FOUND' : field.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {!isEditing && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleStartEdit(field.field, displayValue)}
                            >
                              <Edit2 className="w-4 h-4 text-zinc-400" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Gap Analysis Questions */}
        {questions.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                Required Information
              </CardTitle>
              <CardDescription>
                These details are essential for generating an accurate RFP. Please confirm or provide the following.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {questions.map((q, idx) => (
                <div key={idx} className="p-4 bg-zinc-50 rounded-lg">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    {q.question}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {(() => {
                    // Check if this field should use master data dropdown
                    const masterOptions = getFieldOptions(q.field);
                    
                    if (masterOptions && masterOptions.length > 0) {
                      return (
                        <Select
                          value={userAnswers[q.field] || ''}
                          onValueChange={(value) => handleAnswerChange(q.field, value)}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {masterOptions.map((opt, i) => (
                              <SelectItem key={i} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    } else if (q.input_type === 'select') {
                      return (
                        <Select
                          value={userAnswers[q.field] || ''}
                          onValueChange={(value) => handleAnswerChange(q.field, value)}
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {q.suggestions.map((s, i) => (
                              <SelectItem key={i} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    } else if (q.input_type === 'date') {
                      return (
                        <Input
                          type="date"
                          value={userAnswers[q.field] || ''}
                          onChange={(e) => handleAnswerChange(q.field, e.target.value)}
                          className="bg-white"
                        />
                      );
                    } else {
                      return (
                        <div>
                          <Input
                            value={userAnswers[q.field] || ''}
                            onChange={(e) => handleAnswerChange(q.field, e.target.value)}
                            placeholder="Enter your answer"
                            className="bg-white"
                          />
                          {q.suggestions.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {q.suggestions.map((s, i) => (
                                <button
                                  key={i}
                                  onClick={() => handleAnswerChange(q.field, s)}
                                  className="px-2 py-1 text-xs bg-white border border-zinc-200 rounded hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                  })()}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(1)}>
            Back to Edit
          </Button>
          <Button 
            onClick={handleConfirmAndCreate}
            disabled={loading}
            data-testid="smart-intake-create-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4 mr-2" />
                Confirm & Create Procurement
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {step === 1 && renderInputStep()}
      {step === 2 && renderReviewStep()}
    </div>
  );
};

export default SmartIntakeWizard;
